import React, {useReducer, useEffect} from 'react'
import ManageEmployees from './ManageEmployees/ManageEmployees'
import MoveEmployee from './MoveEmployee/MoveEmployee'
import EmployeeLeaving from './EmployeeLeaving/EmployeeLeaving'
import Instructions from './Instructions/Instructions'
import {requestGroupMove, requestIndividualMove} from '../../../../api/move'
import styled from 'styled-components'
import {DASHBOARD} from '../../../../constants/const'
import Clock from '../../../Clock/Clock'
import HeaderTitle from '../../../Header/HeaderTitle'
import Header from '../../../Header/Header'
import HeaderButton from '../../../Header/HeaderButton'
import {useViewState} from '../../../../hooks/form-state'
import {useUIDispatch} from '../../../../context/UIContext'
import {_L, useLocaleState} from '../../../../context/LocaleContext'
import MediaQuery from 'react-responsive'
import * as ReactGA from 'react-ga'

/**
 * Move types.
 */
export const MOVE_NEW_HIRE = 'MOVE_NEW_HIRE'
export const MOVE_GROUP = 'MOVE_GROUP'
export const MOVE_SPACE_CHANGE = 'MOVE_SPACE_CHANGE'
export const MOVE_EMPLOYEE_LEAVING = 'MOVE_EMPLOYEE_LEAVING'

/**
 * Move sequence forms.
 */
export const FORM_MANAGE_EMPLOYEES = 'FORM_MANAGE_EMPLOYEES'
export const FORM_MOVE_EMPLOYEE = 'FORM_MOVE_EMPLOYEE'
export const FORM_EMPLOYEE_LEAVING = 'FORM_EMPLOYEE_LEAVING'
export const FORM_SPECIAL_INSTRUCTIONS = 'FORM_SPECIAL_INSTRUCTIONS'

const updateObjectInArray = (array, action) => {
  return array.map((item, index) => {
    if (index !== action.index) {
      // This isn't the item we care about - keep it as-is
      return item
    }

    // Otherwise, this is the one we want - return an updated value
    return {
      ...item,
      ...action.item
    }
  })
}

// TODO: move to util module
const insertItem = (array, action) => {
  let newArray = array.slice()
  newArray.splice(action.index, 0, action.item)
  return newArray
}

const getInitialMoveEmployee = ({employee}) => {
  let initialMoveEmployee = {}

  if (employee) {
    const {blId, blName, flId, emId, rmId, blSiteId} = employee
    initialMoveEmployee = {
      building: {value: blId, name: blName},
      floor: {value: flId, name: flId},
      room: {value: rmId, name: rmId},
      employee: {value: emId, name: emId},
      department: {value: '', name: ''},
      siteId: blSiteId,
      startDate: new Date()
    }
  }

  return initialMoveEmployee
}

const initialEmployee = ({user, type, emId, employee}) => {
  const {dpName, deptCode} = user.employee

  let state = {
    building: {value: '', name: ''},
    floor: {value: '', name: ''},
    room: {value: '', name: ''},
    employee: {value: '', name: ''},
    type,
    siteId: '',
    startDate: new Date(),
    endDate: new Date(),
    deptContact: emId,
    requestor: emId
  }

  let emState = {}
  if (type === MOVE_SPACE_CHANGE || type === MOVE_EMPLOYEE_LEAVING) {
    if(employee) {
      const {blId, blName, emId, blSiteId} = employee
      emState = {
        building: {value: blId, name: blName},
        employee: {value: emId, name: emId},
        type,
        siteId: blSiteId,
        startDate: new Date(),
        endDate: new Date()
      }
    }
  }

  return {
    ...state,
    ...emState,
    ...{department: {value: deptCode, name: dpName}}
  }
}

const initialState = {
  equipment: {
    value: '',
    name: ''
  },
  department: {
    value: '',
    name: ''
  },
  startDate: new Date(),
  type: null,
  isSaving: false,
  isProject: false,
  projectId: '',
  instructions: '',
  description: '',
  contact: 'TBD',
  deptContact: '',
  requestor: '',
  moveFurniture: false,
  moveEquipment: false,
  numEmployees: 1,
  moveEmployees: [
    {
      building: {value: '', name: ''},
      floor: {value: '', name: ''},
      room: {value: '', name: ''},
      employee: {value: '', name: ''},
      department: {value: '', name: ''},
      siteId: '',
      startDate: new Date()
    }
  ],
  activeMoveEmployeeIndex: 0
}

const reducer = (state, action) => {
  switch (action.type) {
    case 'setValue':
      return {
        ...state,
        ...action.payload
      }
    case 'setIsSaving':
      return {
        ...state,
        isSaving: action.payload
      }
    case 'addEmployee':
      return {
        ...state,
        moveEmployees: insertItem(state.moveEmployees, {
          item: action.payload,
          index: state.activeMoveEmployeeIndex + 1
        }),
        activeMoveEmployeeIndex: state.activeMoveEmployeeIndex + 1
      }
    case 'updateEmployee':
      return {
        ...state,
        moveEmployees: updateObjectInArray(state.moveEmployees, action.payload)
      }

    case 'decrementActiveIndex':
      return {
        ...state,
        activeMoveEmployeeIndex: (state.activeMoveEmployeeIndex -= 1)
      }
  }
}

const MoveSequence = ({user, type, parentPage, employee = null}) => {
  const emId = user.employee.id
  const [state, dispatch] = useReducer(reducer, initialState)
  const uiDispatch = useUIDispatch()
  const localeState = useLocaleState()

  const typeMap = {
    MOVE_SPACE_CHANGE: FORM_MOVE_EMPLOYEE,
    MOVE_GROUP: FORM_MANAGE_EMPLOYEES,
    MOVE_NEW_HIRE: FORM_MANAGE_EMPLOYEES,
    MOVE_EMPLOYEE_LEAVING: FORM_EMPLOYEE_LEAVING
  }

  const viewStates = {
    FORM_MOVE_EMPLOYEE: {
      prev: () => {
        const {activeMoveEmployeeIndex} = state
        // TODO: check type
        if (activeMoveEmployeeIndex === 0) {
          uiDispatch({
            type: 'setContent',
            payload: {content: parentPage || DASHBOARD}
          })
        } else {
          dispatch({type: 'decrementActiveIndex'})
          return FORM_MOVE_EMPLOYEE
        }
      },
      next: () => {
        const {numEmployees, activeMoveEmployeeIndex} = state
        if (activeMoveEmployeeIndex < numEmployees - 1) {
          let payload = initialEmployee({user, type, employee})
          dispatch({type: 'addEmployee', payload})
          return FORM_MOVE_EMPLOYEE
        } else {
          return FORM_SPECIAL_INSTRUCTIONS
        }
      }
    },
    FORM_SPECIAL_INSTRUCTIONS: {
      prev: () => FORM_MOVE_EMPLOYEE,
      next: () => {
        submitMove()
        return null
      }
    },
    FORM_EMPLOYEE_LEAVING: {
      prev: () =>
        uiDispatch({
          type: 'setContent',
          payload: {content: parentPage || DASHBOARD}
        }),
      next: () => FORM_SPECIAL_INSTRUCTIONS
    },
    FORM_MANAGE_EMPLOYEES: {
      prev: () =>
        uiDispatch({
          type: 'setContent',
          payload: {content: parentPage || DASHBOARD}
        }),
      next: () => FORM_MOVE_EMPLOYEE
    }
  }

  const initialView = typeMap[type]

  const {viewState, dispatchViewState} = useViewState({
    viewStates,
    initialView
  })

  useEffect(
    () => {
      let payload = initialEmployee({user, type, emId, employee})
      dispatch({
        type: 'updateEmployee',
        payload: {item: payload, index: 0}
      })

      const {deptContact, department, requestor} = payload
      dispatch({
        type: 'setValue',
        payload: {
          department,
          deptContact,
          requestor,
          type,
          employee: {value: emId, name: emId}
        }
      })
    },
    [type, user, emId, employee]
  )

  const isIndividualMove = ({moveEmployees}) =>
    type === MOVE_SPACE_CHANGE ||
    (type === MOVE_NEW_HIRE && moveEmployees.length === 1) ||
    type === MOVE_EMPLOYEE_LEAVING

  const submitMove = async () => {
    dispatch({type: 'setIsSaving', payload: true})
    const {moveEmployees} = state

    if (isIndividualMove({moveEmployees})) {
      try {
        await requestIndividualMove({
          move: state,
          ruleName: 'AbEssentialFacility-MoveService-addIndividualMove',
          user,
          isMoveProject: type === MOVE_NEW_HIRE,
          localeState
        })

        dispatch({type: 'setIsSaving', payload: false})

        uiDispatch({
          type: 'setContent',
          payload: {content: parentPage || DASHBOARD}
        })

        ReactGA.event({
          category: 'Move Request',
          action: 'Create Individual Move Request'
        })
      } catch (error) {
        dispatch({type: 'setIsSaving', payload: false})
        alert(error)
      }
    } else {
      if (
        type === MOVE_GROUP ||
        (type === MOVE_NEW_HIRE && moveEmployees.length > 1)
      ) {
        try {
          await requestGroupMove({move: state, user, localeState})
          dispatch({type: 'setIsSaving', payload: false})

          uiDispatch({
            type: 'setContent',
            payload: {content: parentPage || DASHBOARD}
          })

          ReactGA.event({
            category: 'Move Request',
            action: 'Create Group Move Request'
          })
        } catch (error) {
          dispatch({type: 'setIsSaving', payload: false})
          alert(error)
        }
      }
    }
  }

  const setValue = value => dispatch({type: 'setValue', payload: value})

  const setEmployeeValue = ({index, item}) =>
    dispatch({type: 'updateEmployee', payload: {index, item}})

  const onNext = () => dispatchViewState({action: 'next'})

  const onPrevious = () => dispatchViewState({action: 'prev'})

  const onHeaderCancel = () =>
    uiDispatch({
      type: 'setContent',
      payload: {content: parentPage || DASHBOARD}
    })

  return (
    <MediaQuery maxWidth={420}>
      {matches => (
        <Container>
          <Header
            leftContent={
              matches ? (
                <HeaderTitle title={_L({key: 'Request Move', localeState})} />
              ) : (
                <Clock />
              )
            }
            centerContent={
              matches ? (
                ''
              ) : (
                <HeaderTitle title={_L({key: 'Request Move', localeState})} />
              )
            }
            rightContent={
              <HeaderButton
                onClick={onHeaderCancel}
                label={_L({key: 'Cancel', localeState})}
              />
            }
          />
          <StyledContent>
            {
              {
                FORM_MANAGE_EMPLOYEES: (
                  <ManageEmployees
                    onNext={onNext}
                    onPrevious={onPrevious}
                    move={state}
                    setValue={setValue}
                  />
                ),
                FORM_MOVE_EMPLOYEE: (
                  <MoveEmployee
                    onNext={onNext}
                    onPrevious={onPrevious}
                    move={state}
                    setValue={setEmployeeValue}
                  />
                ),
                FORM_EMPLOYEE_LEAVING: (
                  <EmployeeLeaving
                    onNext={onNext}
                    onPrevious={onPrevious}
                    move={state}
                    setValue={setValue}
                  />
                ),
                FORM_SPECIAL_INSTRUCTIONS: (
                  <Instructions
                    onNext={onNext}
                    onPrevious={onPrevious}
                    move={state}
                    setValue={setValue}
                  />
                )
              }[viewState]
            }
          </StyledContent>
        </Container>
      )}
    </MediaQuery>
  )
}

export default MoveSequence

const Container = styled.div`
  width: 100vw;
  height: 100vh;
  background-color: #eff0f3;
`

const StyledContent = styled.div`
  display: flex;
  justify-content: center;
  align-items: flex-start;
  width: 100vw;
  height: calc(100vh - 80px);
  background-color: #eff0f3;
  box-sizing: border-box;
  overflow-y: scroll;
  padding: 34px 0 40px 0;

  @media (max-width: 420px) {
    padding: 0;
  }
`
